import { api } from 'common';

export const UpdateNotableObjectDocumentationMarkdown = `
# Update Notable Object

This block updates a record in Notable's database.

## When to use this block

Use this block when the flow needs to update a record in Notable's database.

## How to use this block

Select the Notable object you want to update (see options below). Based on the object selected, the block will display the appropriate configuration fields.

### Supported objects

We support the following objects:

#### Patient Order

Update a Patient Order in Notable's database using an identifier.

#### Payment

Update an encounter's payment amount in Notable's database.

#### Provider

Update a Provider in Notable's database. When Provider Activation Mode is set to "Activate", this block will:
- Change the provider's kind from Provisional to Active
- Create scheduled work for all encounters associated with the provider
- Optionally configure the provider's preference for auto-applying previsit form templates

##### Input Parameters
- ehrId: Provider's EHR ID
- firstName: Provider's first name
- lastName: Provider's last name
- suffix (optional): Provider's suffix (e.g. MD). Pick from available options or type a custom one
- npi (optional): Provider's NPI (National Provider Identifier)
- email: Provider's email address
- hl7Id (optional): Provider's HL7 ID. When provided, creates or updates the provider's HL7 association for the practice
- apiId (optional): Provider's API ID. When provided, creates or updates the provider's API association for the practice
- providerActivationMode (optional): Provider activation mode - "UpdateOnly" (default) or "Activate". When set to "Activate", changes provider kind from Provisional to Active and creates scheduled work
- providerUserId (required when providerActivationMode is "Activate"): Notable user ID for the provider being activated
- previsitFormTemplatesMode (optional, only when providerActivationMode is "Activate"): Previsit form templates mode - "AutoApply" or "ManualApply" (default). Controls whether previsit form templates are automatically applied for this provider
- exportFingerprintNoteMode (optional, only when providerActivationMode is "Activate"): Export fingerprint note mode - "DISABLED" (default), "ON_PREVISIT_SUBMIT", "ON_CHECK_IN", or "ON_READY_FOR_PROVIDER". Controls when the fingerprint note is exported for this provider

#### Patient

Update a Patient in Notable's database.

##### Input Parameters
Payload:

\`\`\`json
{
  "firstName": "string - required",
  "lastName": "string - required",
  "dateOfBirth": "string - required i.e. valid date in ISO 8601 formats -> 2000-01-01",
  "phoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "cellPhoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "homePhoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "workPhoneNumber": "string - optional i.e. valid US phone number -> 800-456-7890",
  "email": "string - optional i.e. valid email address -> johndoe@example.com",
  "preferredLanguage": "string - optional (enum Language) i.e. ISO 639-1 Language Codes -> en for English",
  "sex": "string - optional (enum Sex)",
  "allowableContactMethods": "array of strings - optional (enum PreferredContactMethod) i.e. ['EMAIL', 'PHONE', 'TEXT']",
  "preferredContactMethod": "string - optional (enum PreferredContactMethod) i.e. PHONE, TEXT, EMAIL', or DO_NOT_CONTACT",
  "preferredPhoneNumber": "string - optional (enum PhoneNumberKind) i.e. CELL, WORK, or HOME",
  "ehrInformationId": "string - required, refers to the ID of the patient entity that is created by Notable",
  "patientEhrId": "string - required, refers to the patient's MRN",

}
\`\`\`

Allowed Enum Values:
- Sex: male, female, other, transgender, other, UNKNOWN
- PreferredContactMethod: DO_NOT_CONTACT, PHONE, TEXT, EMAIL
- PhoneNumberKind: CELL, WORK, HOME
- ContactMethod: EMAIL, PHONE, TEXT
- Language: ${Object.values(api.Language.Language).join(', ')}


#### Patient Insurance Information

Update a Patient's Insurance Information in Notable's database.

##### Input Parameters
Payload:

\`\`\`json
{
  "patientInsuranceInformationId": "string - required, refers to the ID of the patient insurance information entity that is created by Notable",
  "planName": "string - required, refers to the name of the insurance plan"
}
\`\`\`

#### Patient Upload

Update an existing Patient Upload record in Notable's database using its upload ID. This is useful for recording that a patient already has a document (e.g. a photo ID) on file so existing previsit logic can show or hide the relevant primitive.

##### Input Parameters
- patientUploadId: string (required) - the ID of the patient upload record to update
- documentType: string (optional, enum DocumentType) - the type of document, e.g. PHOTO_ID, INSURANCE_CARD
- status: string (optional, enum UploadStatus) - the upload status, e.g. EXISTING_DOCUMENT, VERIFIED
- existingDocument: string (optional) - "true" or "false"; whether the document already exists on file in the EHR
- existingDocumentUploadDate: string | null (optional) - the date the existing document was last updated in the EHR, in ISO 8601 format i.e. 2025-05-13 (use null to clear)

##### Allowed values:
- documentType: ${Object.values(api.PatientUpload.DocumentType).join(', ')}
- status: ${Object.values(api.PatientUpload.UploadStatus).join(', ')}

##### How to retrieve certain values (examples):
- patientUploadId: GetNotableObject block for Patient Uploads

#### Encounter

Update an Encounter in Notable's database based on input in the configuration from a previous block.

##### Input Parameters
Payload:
\`\`\`json
{
  "ehrReferenceValue": "string (optional) - refers to the CSN, FIN, or Encounter ID of an appointment",
  "encounterId": "string (required) - refers to the ID of the encounter entity that is created by Notable which represents an appointment for a patient",
  "isClosed": "boolean (optional) - refers to whether or not the encounter is closed",
  "providerUserId": "string (optional) - refers to the ID of a user entity that is created by Notable for the encounter's provider",
  "schedulingSource": "string (optional) - refers to the scheduling source of the encounter",
  "startTime": "string (optional) - refers to the encounter start time which must be a valid date with timestamp in ISO 8601 formats with the correct timezone offset i.e. 2025-05-13T10:30:00-05:00 for America/Chicago",
  "annotations": [
    {
      "text": "string (required) - the text content of the annotation",
      "type": "string (required, enum EncounterAnnotationType) - the type of the annotation"
    }
  ]
}
\`\`\`

\`annotations\` is optional. Provide it as an array of annotation objects. Up to 10 items are allowed. A single annotation object is also accepted and will be cast to a one-element array.

##### Allowed values:
- schedulingSource: ${Object.values(api.Encounter.SchedulingSource).join(', ')}
- EncounterAnnotationType: GeneralComment, Note

##### How to retrieve certain values (examples):
- encounterId: GetNotableObject block for Encounters
- providerUserId: GetNotableObject block for Providers
`;
